import { useMap } from "@vis.gl/react-maplibre";
import { useEffect, useRef } from "react";
import { PARTICLE_SPEED_STOPS } from "../../lib/mapConfig";
import type { WindGridSlice } from "../../lib/windGrid";

// ─── Config ──────────────────────────────────────────────────────────────────

const PARTICLE_COUNT = 8000;
const MAX_AGE = 120;
const SPEED_SCALE = 0.018;
const FADE_ALPHA = 0.93;
const LINE_WIDTH = 1.4;

// ─── Colour interpolation ────────────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function particleColor(speedMs: number): string {
  const kn = speedMs * 1.944;
  const stops = PARTICLE_SPEED_STOPS;
  if (kn <= (stops[0]?.[0] ?? 0)) {
    const s = stops[0];
    return s ? `rgba(${hexToRgb(s[1]).join(",")},0.7)` : "rgba(80,120,200,0.7)";
  }
  const last = stops[stops.length - 1];
  if (!last || kn >= last[0]) {
    return last
      ? `rgba(${hexToRgb(last[1]).join(",")},0.9)`
      : "rgba(255,34,0,0.9)";
  }
  for (let i = 1; i < stops.length; i++) {
    const lo = stops[i - 1];
    const hi = stops[i];
    if (!lo || !hi) continue;
    if (kn <= hi[0]) {
      const t = (kn - lo[0]) / (hi[0] - lo[0]);
      const [r1, g1, b1] = hexToRgb(lo[1]);
      const [r2, g2, b2] = hexToRgb(hi[1]);
      const r = Math.round(r1 + t * (r2 - r1));
      const g = Math.round(g1 + t * (g2 - g1));
      const b = Math.round(b1 + t * (b2 - b1));
      const a = 0.55 + t * 0.35;
      return `rgba(${r},${g},${b},${a})`;
    }
  }
  return "rgba(80,120,200,0.7)";
}

// ─── Bilinear interpolation ───────────────────────────────────────────────────

interface InterpolatedWind {
  u: number;
  v: number;
  speed: number;
}

function buildInterpolator(slice: WindGridSlice) {
  if (slice.length === 0) return () => ({ u: 0, v: 0, speed: 0 });

  const latSet = [...new Set(slice.map((p) => p.lat))].sort((a, b) => a - b);
  const lonSet = [...new Set(slice.map((p) => p.lon))].sort((a, b) => a - b);

  const lookup = new Map<number, Map<number, { u: number; v: number }>>();
  for (const pt of slice) {
    if (!lookup.has(pt.lat)) lookup.set(pt.lat, new Map());
    lookup.get(pt.lat)!.set(pt.lon, { u: pt.u, v: pt.v });
  }

  return function interpolate(lat: number, lon: number): InterpolatedWind {
    const clampedLat = Math.max(latSet[0] ?? lat, Math.min(latSet[latSet.length - 1] ?? lat, lat));
    const clampedLon = Math.max(lonSet[0] ?? lon, Math.min(lonSet[lonSet.length - 1] ?? lon, lon));

    let latLo = 0;
    for (let i = 0; i < latSet.length - 1; i++) {
      if ((latSet[i] ?? 0) <= clampedLat) latLo = i;
    }
    let lonLo = 0;
    for (let i = 0; i < lonSet.length - 1; i++) {
      if ((lonSet[i] ?? 0) <= clampedLon) lonLo = i;
    }

    const lat0 = latSet[latLo] ?? clampedLat;
    const lat1 = latSet[latLo + 1] ?? clampedLat;
    const lon0 = lonSet[lonLo] ?? clampedLon;
    const lon1 = lonSet[lonLo + 1] ?? clampedLon;

    const tLat = lat1 !== lat0 ? (clampedLat - lat0) / (lat1 - lat0) : 0;
    const tLon = lon1 !== lon0 ? (clampedLon - lon0) / (lon1 - lon0) : 0;

    const g = (la: number, lo: number) => lookup.get(la)?.get(lo) ?? { u: 0, v: 0 };
    const q00 = g(lat0, lon0);
    const q01 = g(lat0, lon1);
    const q10 = g(lat1, lon0);
    const q11 = g(lat1, lon1);

    const u =
      q00.u * (1 - tLat) * (1 - tLon) +
      q10.u * tLat * (1 - tLon) +
      q01.u * (1 - tLat) * tLon +
      q11.u * tLat * tLon;
    const v =
      q00.v * (1 - tLat) * (1 - tLon) +
      q10.v * tLat * (1 - tLon) +
      q01.v * (1 - tLat) * tLon +
      q11.v * tLat * tLon;

    return { u, v, speed: Math.sqrt(u * u + v * v) };
  };
}

// ─── Particle state ───────────────────────────────────────────────────────────

interface Particle {
  lon: number;
  lat: number;
  age: number;
}

// Wider than Sweden's own bbox so particles cover the full visible map viewport
// (at zoom 4.2, the camera shows roughly lon 2–31° and lat 54–71°).
const SPAWN_LON_MIN = 2.0, SPAWN_LON_MAX = 31.0;
const SPAWN_LAT_MIN = 54.0, SPAWN_LAT_MAX = 71.5;

function randomInBounds(): { lon: number; lat: number } {
  return {
    lon: SPAWN_LON_MIN + Math.random() * (SPAWN_LON_MAX - SPAWN_LON_MIN),
    lat: SPAWN_LAT_MIN + Math.random() * (SPAWN_LAT_MAX - SPAWN_LAT_MIN),
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface WindParticlesProps {
  slice: WindGridSlice | null;
}

export function WindParticles({ slice }: WindParticlesProps) {
  const { current: map } = useMap();
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number>(0);
  const interpolateRef = useRef<ReturnType<typeof buildInterpolator> | null>(null);

  // Update interpolator when slice changes.
  useEffect(() => {
    interpolateRef.current = slice ? buildInterpolator(slice) : null;
  }, [slice]);

  // Create canvas as a DOM child of the map container and run the rAF loop.
  useEffect(() => {
    if (!map) return;
    const mapInstance = map;

    // Create and append canvas directly to the map container so it overlays the GL canvas.
    const canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:absolute;top:0;left:0;pointer-events:none;z-index:2;";
    const container = mapInstance.getContainer();
    container.appendChild(canvas);

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = container.offsetWidth * dpr;
      canvas.height = container.offsetHeight * dpr;
      canvas.style.width = `${container.offsetWidth}px`;
      canvas.style.height = `${container.offsetHeight}px`;
    };
    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(container);

    if (particlesRef.current.length === 0) {
      particlesRef.current = Array.from({ length: PARTICLE_COUNT }, () => ({
        ...randomInBounds(),
        age: Math.floor(Math.random() * MAX_AGE),
      }));
    }

    const ctx = canvas.getContext("2d")!;

    function tick() {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width;
      const h = canvas.height;

      ctx.globalCompositeOperation = "destination-in";
      ctx.fillStyle = `rgba(0,0,0,${FADE_ALPHA})`;
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = "source-over";

      const interpolate = interpolateRef.current;
      if (!interpolate) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      ctx.lineWidth = LINE_WIDTH * dpr;
      ctx.lineCap = "round";

      // Precompute pixel-per-degree scale once per frame at a reference point.
      // This cuts project() calls from 3 per particle to 1 per particle.
      // cos(lat) correction is applied per particle for E-W accuracy.
      const REF_LON = 16.5, REF_LAT = 62.5;
      const { x: r0x, y: r0y } = mapInstance.project([REF_LON, REF_LAT]);
      const { x: r1x } = mapInstance.project([REF_LON + 0.01, REF_LAT]);
      const { y: r2y } = mapInstance.project([REF_LON, REF_LAT + 0.01]);
      const pxPerDegLonRef = Math.abs(r1x - r0x) / 0.01; // px/° at REF_LAT
      const pxPerDegLat = Math.abs(r2y - r0y) / 0.01;    // px/° latitude (Mercator ≈ const in small range)
      const cosRef = Math.cos(REF_LAT * Math.PI / 180);
      // silence unused-var lint (r0x/r0y are the anchor; only differences matter)
      void r0x; void r0y;

      for (const p of particlesRef.current) {
        const wind = interpolate(p.lat, p.lon);

        const { x: px, y: py } = mapInstance.project([p.lon, p.lat]);

        // Latitude-corrected pixel scale for this particle
        const cosLat = Math.cos(p.lat * Math.PI / 180);
        const pxPerDegLon = pxPerDegLonRef * cosLat / cosRef;

        // Convert m/s → pixel displacement (SPEED_SCALE is a visual tuning factor)
        let dx = wind.u * SPEED_SCALE * pxPerDegLon;
        let dy = -wind.v * SPEED_SCALE * pxPerDegLat;

        // Guarantee a minimum trail length so calm-wind particles are always visible.
        const MIN_PX = 1.8;
        const speedPx = Math.sqrt(dx * dx + dy * dy);
        if (speedPx < MIN_PX) {
          if (speedPx < 0.001) {
            // Truly zero wind: rotate slowly so particles don't stack invisibly
            const angle = (p.age * 0.13) % (2 * Math.PI);
            dx = MIN_PX * Math.cos(angle);
            dy = MIN_PX * Math.sin(angle);
          } else {
            const s = MIN_PX / speedPx;
            dx *= s;
            dy *= s;
          }
        }

        const sx = px * dpr;
        const sy = py * dpr;
        const tx = (px + dx) * dpr;
        const ty = (py + dy) * dpr;

        if (sx >= 0 && sx <= w && sy >= 0 && sy <= h) {
          ctx.strokeStyle = particleColor(wind.speed);
          ctx.beginPath();
          ctx.moveTo(sx, sy);
          ctx.lineTo(tx, ty);
          ctx.stroke();
        }

        // Geo-position advance: same formula as before (mPerPxEast cancels)
        p.lon += wind.u * SPEED_SCALE;
        p.lat += wind.v * SPEED_SCALE;
        p.age += 1;

        if (
          p.age > MAX_AGE ||
          p.lon < SPAWN_LON_MIN - 1 ||
          p.lon > SPAWN_LON_MAX + 1 ||
          p.lat < SPAWN_LAT_MIN - 0.5 ||
          p.lat > SPAWN_LAT_MAX + 0.5
        ) {
          const fresh = randomInBounds();
          p.lon = fresh.lon;
          p.lat = fresh.lat;
          p.age = 0;
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      canvas.remove();
    };
  }, [map]);

  // This component manages its canvas imperatively — nothing to render into React tree.
  return null;
}
